import React, { useState, useEffect } from "react";
// import BaseCard from '../../components/features/dashboard/BaseCard'
import InputPrimaryComponents from '../../components/common/inputPrimaryComponents';
import { useForm, Controller } from "react-hook-form";
import { Panel } from "primereact/panel";
import { DevTool } from "@hookform/devtools";
import { showToast } from '../../components/common/toast';
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../constants/toast";
import { commonPostApi } from "../../utils/apiService";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import AddButtonSection from '../../components/common/AddButtonSection';
import "../../assets/styles/examination.scss";

// Constants for API
const EYE_EXAM_ENDPOINTS = {
  ADD: API_ENDPOINTS.ADD_EYE_EXAMINATION_DETAILS,
  EDIT: API_ENDPOINTS.EDIT_EYE_EXAMINATION_DETAILS,
  SEARCH: API_ENDPOINTS.SEARCH_EYE_EXAMINATION_DETAILS
};

const API_HEADERS = {
  "Content-Type": "application/json"
};

// Form Field Configurations
const ADDITIONAL_EXAMINATION_FIELDS = [
  {
    type: "text",
    name: "auto_refraction",
    label: "Auto Refraction",
    fieldLabel: "auto_refraction",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "refraction_dilated",
    label: "Refraction (Dilated)",
    fieldLabel: "refraction_dilated",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "pgp",
    label: "PGP",
    fieldLabel: "pgp",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
  {
    type: "dropdown",
    name: "glasses_prescription",
    label: "Glasses Prescription",
    fieldLabel: "glasses_prescription",
    placeholder: "Select",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "intermediate_glasses_prescription",
    label: "Intermediate Glasses Prescription",
    fieldLabel: "intermediate_glasses_prescription",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "pmt",
    label: "PMT",
    fieldLabel: "pmt",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "retinoscopy",
    label: "Retinoscopy",
    fieldLabel: "retinoscopy",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "keratometry",
    label: "Keratometry",
    fieldLabel: "keratometry",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "amsler",
    label: "Amsler",
    fieldLabel: "amsler",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "contact_lens_prescription",
    label: "Contact Lens Prescription",
    fieldLabel: "contact_lens_prescription",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "color_vision",
    label: "Color Vision",
    fieldLabel: "color_vision",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "contrast_sensitivity",
    label: "Contrast Sensitivity",
    fieldLabel: "contrast_sensitivity",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "orthoptics",
    label: "Orthoptics",
    fieldLabel: "orthoptics",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
];

const PRIMARY_EXAMINATION_FIELDS = [
  {
    type: "text",
    name: "visual_acuity",
    label: "Visual Acuity",
    fieldLabel: "visual_acuity",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "intraocular_pressure",
    label: "Intraocular Pressure",
    fieldLabel: "intraocular_pressure",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
];

const RECOMMENDATION_FIELDS = [
  {
    type: "dropdown",
    name: "surgery_recommended",
    label: "Surgery Recommended",
    fieldLabel: "surgery_recommended",
    placeholder: "Select",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    inputParentClass: "pb-4",
  },
  {
    type: "text",
    name: "others",
    label: "Other",
    fieldLabel: "others",
    placeholder: "Type here",
    inputParentClass: "pb-4",
  },
];

// Form Field Mappings - FIXED
const FORM_FIELDS = [
  // Left eye fields
  { name: "L_distance_sph", index: "distance_sph" },
  { name: "L_distance_vision", index: "distance_vision" },
  { name: "L_distance_necylar", index: "distance_necylar" },
  { name: "L_distance_axis", index: "distance_axis" },
  { name: "L_near_sph", index: "near_sph" },
  { name: "L_near_vision", index: "near_vision" },
  { name: "L_near_necylar", index: "near_necylar" },
  { name: "L_near_axis", index: "near_axis" },
  { name: "L_intraocular_pressure", index: "iop" },
  { name: "L_visual_acuity", index: "va" },
  { name: "L_auto_refraction", index: "auto_refraction" },
  { name: "L_contrast_sensitivity", index: "contrast_sensitivity" },
  { name: "L_refraction_dilated", index: "refraction_dilated" },
  { name: "L_orthoptics", index: "orthoptics" },
  { name: "L_pgp", index: "pgp" },
  { name: "L_intermediate_glasses_prescription", index: "intermediate_glasses_prescription" },
  { name: "L_pmt", index: "pmt" },
  { name: "L_retinoscopy", index: "retinoscopy" },
  { name: "L_keratometry", index: "keratometry" },
  { name: "L_contact_lens_prescription", index: "contact_lens_prescription" },
  { name: "L_amsler", index: "amsler" },
  { name: "L_color_vision", index: "color_vision" },
  
  // Right eye fields - FIXED field names
  { name: "R_visual_acuity", index: "va" },
  { name: "R_auto_refraction", index: "auto_refraction" },
  { name: "R_intraocular_pressure", index: "iop" },
  { name: "R_contrast_sensitivity", index: "contrast_sensitivity" },
  { name: "R_refraction_dilated", index: "refraction_dilated" },
  { name: "R_orthoptics", index: "orthoptics" },
  { name: "R_pgp", index: "pgp" },
  { name: "R_intermediate_glasses_prescription", index: "intermediate_glasses_prescription" },
  { name: "R_pmt", index: "pmt" },
  { name: "R_retinoscopy", index: "retinoscopy" },
  { name: "R_keratometry", index: "keratometry" },
  { name: "R_contact_lens_prescription", index: "contact_lens_prescription" },
  { name: "R_amsler", index: "amsler" },
  { name: "R_color_vision", index: "color_vision" },
  
  // Right eye refraction table fields - FIXED
  { name: "R_distance_sph", index: "distance_sph" },
  { name: "R_distance_vision", index: "distance_vision" },
  { name: "R_distance_necylar", index: "distance_necylar" },
  { name: "R_distance_axis", index: "distance_axis" },
  { name: "R_near_sph", index: "near_sph" },
  { name: "R_near_vision", index: "near_vision" },
  { name: "R_near_necylar", index: "near_necylar" },
  { name: "R_near_axis", index: "near_axis" },
  
  // Common fields
  { name: "others", index: "others" },
];

const BOOLEAN_FIELDS = [
  { name: "surgery_recommended", index: "surgery_recommended" },
  { name: "R_glasses_prescription", index: "glasses_prescription" },
  { name: "L_glasses_prescription", index: "glasses_prescription" },
];

// Component Definitions
const RefractionInput = ({ name, control }) => (
  <Controller
    name={name}
    control={control}
    defaultValue=""
    render={({ field }) => (
      <input
        {...field}
        type="text"
        className="w-full p-2 text-center rounded-md border-solid border-1 border-round-md border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary text-sm transition-all duration-200 inter-500 color-grey-15"
        style={{
          minWidth: "80px",
          height: "36px",
          background: "transparent",
        }}
      />
    )}
  />
);

const TableHeader = ({ headers }) => (
  <thead>
    <tr className="flex gap-4 border-y-1 border-gray-200" >
      {headers.map((header, index) => (
        <th
          key={header}
          className="flex-1 text-left p-3 text-sm font-semibold text-gray-500 relative"
        >
          {header}
          {index < headers.length - 1 && (
            <div 
              className="absolute right-0"
              style={{ 
                borderRight: '2px solid #d1d5db',
                height: '16px',
                top: '50%',
                transform: 'translateY(-50%)'
              }}
            />
          )}
        </th>
      ))}
    </tr>
  </thead>
);

const TableRow = ({ rowType, headers, control, side, isLastRow }) => (
  <tr className={`flex gap-4 mb-2 ${!isLastRow ? 'border-bottom-1 border-gray-200' : ''}`}>
    <td className="flex-1 text-left p-3 text-sm text-gray-600">{rowType}</td>
    {[...Array(4)].map((_, index) => (
      <td key={index} className="flex-1 p-3">
        <RefractionInput
          control={control}
          name={`${side}_${rowType.toLowerCase()}_${headers[index + 1].toLowerCase()}`}
        />
      </td>
    ))}
  </tr>
);

const RefractionTable = ({ control, errors, side }) => {
  const headers = ["TYPE", "SPH", "NECYLAR", "AXIS", "VISION"];
  const rows = ["Distance", "Near"];

  return (
    <div className="overflow-x-auto" style={{ boxShadow: '0px 3px 12px 0px rgba(47, 43, 61, 0.14)' }}>
      <div className="flex flex-column p-3 font-medium">Dry Refraction</div>
      <table className="w-full border-collapse">
        <TableHeader headers={headers} />
        <tbody>
          {rows.map((rowType, index) => (
            <TableRow
              key={rowType}
              rowType={rowType}
              headers={headers}
              control={control}
              side={side}
              isLastRow={index === rows.length - 1}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AdditionalFields = ({ commonInputProps, side }) => (
  <Panel
    header="More Options"
    toggleable
    expandIcon={<i className="pi pi-chevron-right" />}
    collapseIcon={<i className="pi pi-chevron-down" />}
    className="primary-box-shadow"
  >
    {ADDITIONAL_EXAMINATION_FIELDS.map((field) => (
      <InputPrimaryComponents
        key={field.name}
        {...commonInputProps}
        {...field}
        name={`${side}_${field.name}`}
      />
    ))}
  </Panel>
);

const EyeSection = ({ side, control, errors }) => {
  const commonInputProps = {
    control,
    errors,
    rules: {},
    value: "",
  };

  return (
    <div className={"w-full md:w-6 flex flex-column gap-4 " + (side === "L" ? " border-bottom-1 md:border-right-1 border-r-dotted border-gray-300 pb-4 md:pb-0 md:pr-4" : "pt-4 md:pt-0 md:pl-4")}>
      <div>{side}/DO</div>
      {PRIMARY_EXAMINATION_FIELDS.map((field) => (
        <InputPrimaryComponents
          key={field.name}
          {...commonInputProps}
          {...field}
          name={`${side}_${field.name}`}
        />
      ))}
      <RefractionTable control={control} errors={errors} side={side} />
      <AdditionalFields commonInputProps={commonInputProps} side={side} />
    </div>
  );
};

const generateDefaultValues = () => {
  const baseValues = {
    L_visual_acuity: "",
    L_intraocular_pressure: "",
    R_visual_acuity: "",
    R_intraocular_pressure: "",
    surgery_recommendation: "",
    other_options: "",
  };

  // Add refraction table default values
  ["Distance", "Near"].forEach((rowType) => {
    ["SPH", "NECYLAR", "AXIS", "VISION"].forEach((header) => {
      ["L", "R"].forEach((side) => {
        baseValues[`${side}_${rowType.toLowerCase()}_${header.toLowerCase()}`] = "";
      });
    });
  });

  // Add additional fields default values
  ADDITIONAL_EXAMINATION_FIELDS.forEach((field) => {
    ["L", "R"].forEach((side) => {
      baseValues[`${side}_${field.name}`] = "";
    });
  });

  return baseValues;
};

// Main Component
function EyeExamNew({ patientVisitId }) {
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [originalFormData,setOriginalFormData] = useState([])

  const {
    formState: { errors, dirtyFields },
    control,
    setValue,
    handleSubmit,
    reset
  } = useForm({
    defaultValues: generateDefaultValues(),
  });

  // Form Handlers
  const onSubmit = async (data) => {
    if (loading) return;
    setLoading(true);

    const eyeExamination = {
      patient_visit_id: patientVisitId,
      eyeTest: [
        createEyeTestData(data, "Left Eye"),
        createEyeTestData(data, "Right Eye"),
      ],
    };

    try {
      const endpoint = edit ? EYE_EXAM_ENDPOINTS.EDIT : EYE_EXAM_ENDPOINTS.ADD;
      const response = await commonPostApi(endpoint, eyeExamination, { headers: API_HEADERS });
      
      if (response?.data || response?.data?.success === true) {
        showToast(TOAST_TYPE.SUCCESS, edit ? FEATURE_MSG.EXAMINATION.EYE_EXAM_UPDATED : FEATURE_MSG.EXAMINATION.EYE_EXAM_ADDED);
        await getEyeExam();
      }
    } catch (err) {
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
    } finally {
      setLoading(false);
    }
  };

  const discard = () => {
    if (originalFormData.left || originalFormData.right) {
      updateFormValues(originalFormData.left, originalFormData.right);
    } else {
      reset(generateDefaultValues());
    }
  };

  const getEyeExam = async () => {
    try {
      const response = await commonPostApi(
        EYE_EXAM_ENDPOINTS.SEARCH,
        { patient_visit_id: patientVisitId },
        { headers: API_HEADERS }
      );

      const formData = response?.data?.data || null;
      if (Object.keys(formData || {}).length !== 0) {
        setEdit(true);
        const leftEyeData = formData.find(
          (item) => item.eye_type === "Left Eye",
        );
        const rightEyeData = formData.find(
          (item) => item.eye_type === "Right Eye",
        );
        setOriginalFormData({ left: leftEyeData, right: rightEyeData });
        updateFormValues(leftEyeData, rightEyeData);

        console.log("Left Eye Data:", leftEyeData); // Debug log
        console.log("Right Eye Data:", rightEyeData); // Debug log
      } else {
        setEdit(false);
        reset(generateDefaultValues());
      }
    } catch (err) {
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
    }
  };

  // FIXED: Updated form values function
  const updateFormValues = (leftEyeData, rightEyeData) => {
    FORM_FIELDS.forEach(field => {
      let eyeData;
      let value = "";
      
      if (field.name.startsWith("L_")) {
        eyeData = leftEyeData;
      } else if (field.name.startsWith("R_")) {
        eyeData = rightEyeData;
      } else {
        // Common fields like 'others' - use either eye data
        eyeData = leftEyeData || rightEyeData;
      }
      
      if (eyeData && eyeData[field.index] !== undefined && eyeData[field.index] !== null) {
        value = eyeData[field.index].toString();
      }
      
      console.log(`Setting ${field.name} = ${value}`); // Debug log
      setValue(field.name, value);
    });

    BOOLEAN_FIELDS.forEach(field => {
      let eyeData;
      let value = "";
      
      if (field.name.startsWith("L_")) {
        eyeData = leftEyeData;
      } else if (field.name.startsWith("R_")) {
        eyeData = rightEyeData;
      } else {
        // Common fields - use either eye data
        eyeData = leftEyeData || rightEyeData;
      }
      
      if (eyeData && eyeData[field.index] !== undefined && eyeData[field.index] !== null) {
        value = eyeData[field.index] === true ? "yes" : "no";
      }
      
      console.log(`Setting boolean ${field.name} = ${value}`); // Debug log
      setValue(field.name, value);
    });
  };

  const createEyeTestData = (data, eyeType) => ({
    eye_type: eyeType,
    iop: data?.[`${eyeType.charAt(0)}_intraocular_pressure`] || null,
    distance_axis: data?.[`${eyeType.charAt(0)}_distance_axis`] || null,
    distance_necylar: data?.[`${eyeType.charAt(0)}_distance_necylar`] || null,
    distance_sph: data?.[`${eyeType.charAt(0)}_distance_sph`] || null,
    distance_vision: data?.[`${eyeType.charAt(0)}_distance_vision`] || null,
    near_axis: data?.[`${eyeType.charAt(0)}_near_axis`] || null,
    near_necylar: data?.[`${eyeType.charAt(0)}_near_necylar`] || null,
    near_sph: data?.[`${eyeType.charAt(0)}_near_sph`] || null,
    near_vision: data?.[`${eyeType.charAt(0)}_near_vision`] || null,
    va: data?.[`${eyeType.charAt(0)}_visual_acuity`] || null,
    auto_refraction: data?.[`${eyeType.charAt(0)}_auto_refraction`] || null,
    contrast_sensitivity: data?.[`${eyeType.charAt(0)}_contrast_sensitivity`] || null,
    refraction_dilated: data?.[`${eyeType.charAt(0)}_refraction_dilated`] || null,
    orthoptics: data?.[`${eyeType.charAt(0)}_orthoptics`] || null,
    glasses_prescription: data?.[`${eyeType.charAt(0)}_glasses_prescription`] === "yes",
    pgp: data?.[`${eyeType.charAt(0)}_pgp`] || null,
    intermediate_glasses_prescription: data?.[`${eyeType.charAt(0)}_intermediate_glasses_prescription`] || null,
    pmt: data?.[`${eyeType.charAt(0)}_pmt`] || null,
    retinoscopy: data?.[`${eyeType.charAt(0)}_retinoscopy`] || null,
    keratometry: data?.[`${eyeType.charAt(0)}_keratometry`] || null,
    contact_lens_prescription: data?.[`${eyeType.charAt(0)}_contact_lens_prescription`] || null,
    amsler: data?.[`${eyeType.charAt(0)}_amsler`] || null,
    color_vision: data?.[`${eyeType.charAt(0)}_color_vision`] || null,
    surgery_recommended: data?.surgery_recommended === "yes",
    others: data?.others || "",
  });

  const submitButtons = [
    {
      type: "primarySuccessButton",
      label: "Save Changes",
      class: " w-6 md:w-11rem  pr-3",
      onClick: handleSubmit(onSubmit),
    },
    {
      type: "secondaryButton",
      label: "Discard",
      class: "w-7rem",
      onClick: discard,
    },
  ];

  useEffect(() => {
    getEyeExam();
  }, [patientVisitId]);

  return (
    <div className="flex flex-column gap-4">
      <div className="flex flex-column md:flex-row">
        <EyeSection side="L" control={control} errors={errors}  />
        <EyeSection side="R" control={control} errors={errors} />
      </div>
      <div className="flex flex-column md:flex-row w-full"  style={{ boxShadow: '0px 3px 12px 0px rgba(47, 43, 61, 0.14)' }}>
        {RECOMMENDATION_FIELDS.map((field) => (
          <InputPrimaryComponents
            key={field.name}
            control={control}
            {...field}
            inputParentClass="w-full md:w-6 p-4"
          />
        ))}
      </div>
      <div className="mt-2 ml-2">
        <AddButtonSection
          disabled={Object.keys(dirtyFields).length === 0 || loading}
          buttons={submitButtons}
        />
      </div>
      <DevTool control={control} />
    </div>
  );
}

export default EyeExamNew;